import * as React from 'react'

import { computed, makeObservable } from 'mobx'
import { inject, observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'

import { PresentationScreenKey } from '~/client/graph'
import DraggableGrid from '~/client/src/desktop/components/DraggableGrid/DraggableGrid'
import NumberSelector from '~/client/src/desktop/components/NumberSelector/NumberSelector'
import DesktopInitialState from '~/client/src/desktop/stores/DesktopInitialState'
import DesktopEventStore from '~/client/src/desktop/stores/EventStore/DesktopEvents.store'
import PresentationModeStore from '~/client/src/desktop/stores/ui/PresentationMode.store'
import { Loader } from '~/client/src/shared/components/Loader'
import KnownTranslatorKeys from '~/client/src/shared/localization/knownTranslatorKeys'
import Localization from '~/client/src/shared/localization/LocalizationManager'

import ConfirmationPresentationDialog from './components/ConfirmationPresentationDialog'
import PresentationLastUpdated from './components/PresentationLastUpdated'
import PresentationModeHeader from './components/PresentationModeHeader'
import PresentationScreenComponent from './components/PresentationScreen'
import PresentationSetUpStore from './PresentationSetUp.store'

interface IProps {
  state?: DesktopInitialState
  eventsStore?: DesktopEventStore
  presentationModeStore?: PresentationModeStore
}

const SCREEN_DATA_MAP = {
  [PresentationScreenKey.Gantt]: {
    titleTranslatorKey: KnownTranslatorKeys.schedule,
    descriptionTranslatorKey:
      KnownTranslatorKeys.presentationModeDescriptions_gantt,
    imageSrc: '/static/images/gantt-screen.png',
  },
  [PresentationScreenKey.Deliveries]: {
    titleTranslatorKey: KnownTranslatorKeys.deliveries,
    descriptionTranslatorKey:
      KnownTranslatorKeys.presentationModeDescriptions_deliveries,
    imageSrc: '/static/images/deliveries-screen.png',
  },
  [PresentationScreenKey.Logistics]: {
    titleTranslatorKey: KnownTranslatorKeys.home,
    descriptionTranslatorKey:
      KnownTranslatorKeys.presentationModeDescriptions_logistics,
    imageSrc: '/static/images/logistics-screen.png',
  },
  [PresentationScreenKey.Forms]: {
    titleTranslatorKey: KnownTranslatorKeys.forms,
    descriptionTranslatorKey: KnownTranslatorKeys.forms,
    imageSrc: '/static/images/logistics-screen.png',
  },
}

@inject('state', 'eventsStore', 'presentationModeStore')
@observer
export default class PresentationMode extends React.Component<IProps> {
  private presentationSetUpStore: PresentationSetUpStore

  public constructor(props: IProps) {
    super(props)
    this.presentationSetUpStore = new PresentationSetUpStore(
      props.state,
      props.eventsStore,
      props.presentationModeStore,
    )
    makeObservable(this)
  }

  public render() {
    const { onItemOrderChanged, onInactivityDurationChanged } =
      this.presentationSetUpStore
    const { isLoading } = this.props.state
    const { inactivityDuration } = this.props.state.presentationSettings

    return (
      <div
        className={classList({
          'tw:p-[30px]': true,
          'tw:!h-full': isLoading,
        })}
      >
        <ConfirmationPresentationDialog store={this.presentationSetUpStore} />
        <PresentationModeHeader
          store={this.presentationSetUpStore}
          isLoading={isLoading}
        />
        {isLoading ? (
          <Loader hint={Localization.translator.loading} />
        ) : (
          <>
            <div className="tw:flex tw:items-center tw:mb-2.5">
              <div className="tw:text-title-md tw:text-foreground">
                {Localization.translator.presentationScreens}
              </div>
              <div>
                <PresentationLastUpdated />
              </div>
            </div>
            <div className="tw:mb-5">
              <div className="tw:inline-block tw:mr-[5px] tw:text-body-md tw:text-foreground">
                {
                  Localization.translator.presentationModeDescriptions
                    .periodOfInactivity
                }
              </div>
              <NumberSelector
                value={inactivityDuration}
                min={1}
                onChange={onInactivityDurationChanged}
                numberFormatter={this.getMinutesNumberString}
              />
            </div>
            <DraggableGrid
              items={this.screenComponents}
              onDragEnd={onItemOrderChanged}
            />
          </>
        )}
      </div>
    )
  }

  @computed
  private get screenComponents() {
    return this.presentationSetUpStore.availableScreens.map(screen => (
      <PresentationScreenComponent
        screen={screen}
        store={this.presentationSetUpStore}
        {...SCREEN_DATA_MAP[screen.key]}
      />
    ))
  }

  private getMinutesNumberString(value: number) {
    return Localization.translator.xMinutes(value)
  }
}
